// FLAGS: none
// The "Spaces Schedules" calendar's data: open sessions, the expert's sign-ups and the public sessions they
// host, placed at their wall-clock time in the session's zone; plus the URL state and filters it keeps.
import {
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { toZonedTime } from "date-fns-tz";
import {
  ResponseConveneHostableSessionResponse,
  ResponseConveneHostedSessionResponse,
} from "@src/api/v1/data-contracts";

/** Open for sign-up, signed up by this expert, or confirmed with this expert as host. */
export type HostEntryKind = "open" | "signed-up" | "hosting";
export type HostCalendarMode = "month" | "week";
export type SessionTypeFilter = "groupsession" | "masterclass";

/**
 * The type of every session open for hosting. The open-for-hosting response has no type because schedules only
 * place group sessions today; when other types are scheduled, the API needs to send it and this goes away.
 */
export const HOSTABLE_SESSION_TYPE: SessionTypeFilter = "groupsession";

export const SESSION_TYPES: SessionTypeFilter[] = [
  "groupsession",
  "masterclass",
];

export interface HostCalendarEntry {
  sessionId: string;
  title: string;
  languageCode: string | null;
  eventType: string;
  kind: HostEntryKind;
  startsAt: string;
  endsAt: string;
  timeZone: string;
  /** The sign-up row behind an open or signed-up entry; null for a session the expert hosts. */
  hostable: ResponseConveneHostableSessionResponse | null;
  /** A session the expert hosts that a schedule placed: they can step down from the dashboard. */
  withdrawable: boolean;
}

export interface HostCalendarEvent {
  title: string;
  start: Date;
  end: Date;
  resource: HostCalendarEntry;
}

export interface HostCalendarState {
  date: Date;
  mode: HostCalendarMode;
  language: string | null;
  type: SessionTypeFilter | null;
  mine: boolean;
}

const WEEK_START = { weekStartsOn: 1 } as const;
const DAY = "yyyy-MM-dd";

export const readHostCalendarState = (
  params: URLSearchParams,
  today: Date,
): HostCalendarState => {
  const date = parseISO(params.get("date") ?? "");
  const type = params.get("type");
  return {
    date: isValid(date) ? date : today,
    mode: params.get("mode") === "week" ? "week" : "month",
    language: params.get("language") || null,
    type: SESSION_TYPES.find((entry) => entry === type) ?? null,
    mine: params.get("mine") === "1",
  };
};

/** Writes the calendar's own params and keeps the rest (the page's ?view=). */
export const writeHostCalendarState = (
  state: HostCalendarState,
  params: URLSearchParams,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  const put = (key: string, value: string | null) =>
    value ? next.set(key, value) : next.delete(key);
  put("date", format(state.date, DAY));
  put("mode", state.mode);
  put("language", state.language);
  put("type", state.type);
  put("mine", state.mine ? "1" : null);
  return next;
};

/** Whole Monday-to-Sunday weeks around the visible month, or the one visible week. */
export const visibleRange = (
  date: Date,
  mode: HostCalendarMode,
): { from: Date; to: Date } =>
  mode === "week"
    ? { from: startOfWeek(date, WEEK_START), to: endOfWeek(date, WEEK_START) }
    : {
        from: startOfWeek(startOfMonth(date), WEEK_START),
        to: endOfWeek(endOfMonth(date), WEEK_START),
      };

/** Picked as host or cohost of a placement on the sign-up list (not a session yet). */
export const isSeated = (session: ResponseConveneHostableSessionResponse) =>
  session.myRole === "host" || session.myRole === "cohost";

const kindOf = (
  session: ResponseConveneHostableSessionResponse,
): HostEntryKind => {
  if (isSeated(session)) return "hosting";
  return session.signedUp ? "signed-up" : "open";
};

/**
 * Sessions the expert hosts win over the sign-up list, so a session never shows twice. A placement they were picked
 * for is "hosting" too, withdrawable while sign-ups are open.
 */
export const toHostEntries = (
  open: ResponseConveneHostableSessionResponse[],
  hosted: ResponseConveneHostedSessionResponse[],
): HostCalendarEntry[] => {
  const hosting: HostCalendarEntry[] = hosted
    .filter(
      (session) =>
        session.audience === "public" && session.status !== "cancelled",
    )
    .map((session) => ({
      sessionId: session.sessionId,
      title: session.title,
      languageCode: session.languageCode ?? null,
      eventType: session.eventType,
      kind: "hosting",
      withdrawable: session.isScheduled,
      startsAt: session.startsAt,
      endsAt: session.endsAt,
      timeZone: session.timeZone,
      hostable: null,
    }));
  const hostingIds = new Set(hosting.map((entry) => entry.sessionId));
  const signups: HostCalendarEntry[] = open
    .filter((session) => !hostingIds.has(session.sessionId))
    .map((session) => ({
      sessionId: session.sessionId,
      title: session.title,
      languageCode: session.languageCode ?? null,
      eventType: HOSTABLE_SESSION_TYPE,
      kind: kindOf(session),
      withdrawable: isSeated(session) && session.canStepDown,
      startsAt: session.startsAt,
      endsAt: session.endsAt,
      timeZone: session.timeZone,
      hostable: session,
    }));
  return [...hosting, ...signups];
};

export const matchesFilters = (
  entry: HostCalendarEntry,
  state: HostCalendarState,
): boolean =>
  (!state.language || entry.languageCode === state.language) &&
  (!state.type || entry.eventType === state.type) &&
  (!state.mine || entry.kind !== "open");

/** Placed at the session's own wall-clock time, whatever the browser's zone. */
export const toCalendarEvent = (
  entry: HostCalendarEntry,
): HostCalendarEvent => ({
  title: entry.title,
  start: toZonedTime(entry.startsAt, entry.timeZone),
  end: toZonedTime(entry.endsAt, entry.timeZone),
  resource: entry,
});

export const kindTone: Record<HostEntryKind, string> = {
  open: "!border-dashed !border-indigo-300 !bg-white !text-indigo-800",
  "signed-up": "!border-amber-300 !bg-amber-100 !text-amber-900",
  hosting: "!border-green-300 !bg-green-100 !text-green-900",
};

export const kindLabelKey: Record<HostEntryKind, string> = {
  open: "HostSignups.Calendar.Kind.Open",
  "signed-up": "HostSignups.Calendar.Kind.SignedUp",
  hosting: "HostSignups.Calendar.Kind.Hosting",
};

export const sessionTypeLabelKey: Record<SessionTypeFilter, string> = {
  groupsession: "HostSignups.Calendar.Type.GroupSession",
  masterclass: "HostSignups.Calendar.Type.Masterclass",
};

/** The list view's filters on an open session: language, type, and "only mine" (signed up for, or picked for). */
export const hostableMatchesFilters = (
  session: ResponseConveneHostableSessionResponse,
  state: Pick<HostCalendarState, "language" | "type" | "mine">,
): boolean =>
  (!state.language || session.languageCode === state.language) &&
  (!state.type || state.type === HOSTABLE_SESSION_TYPE) &&
  (!state.mine || session.signedUp || isSeated(session));
